import React, { Component } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { colors, distances } from '../../../styles/constants';
import Page, { Back } from '../../../components/Page';
import Card from '../../../components/Card';
import { LoadingOverlay } from '../../../components/Loading';
import { H1 } from '../../../components/Typography';
import { DraftOrder } from '../../../types/orders';

import DraftOrderId from './DraftOrderId';
import Items from '../../common/Items';
import Customer from '../../common/Customer';
import DraftOrderHistory from './DraftOrderHistory';
import Itinerary from '../../common/Itinerary';
import Metadata from '../../common/Metadata';
import ExternalLinks from '../../common/ExternalLinks';
import NotFound from '../../../components/NotFound';


interface MatchParams {
    accountId: string;
    draftOrderId: string;
}

export interface DraftOrderPageProps extends WithTranslation, RouteComponentProps<MatchParams> {
    getDraftOrder: (accountId: string, id: string) => void;
    draft: DraftOrder | undefined;
    isLoading: boolean;
    isNotFound: boolean;
}

class DraftOrderPage extends Component<DraftOrderPageProps> {
    componentDidMount() {
        this.props.getDraftOrder(this.props.match.params.accountId, this.props.match.params.draftOrderId);
    }

    componentDidUpdate(prevProps: Readonly<DraftOrderPageProps>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.match.params.draftOrderId !== prevProps.match.params.draftOrderId) {
            this.props.getDraftOrder(this.props.match.params.accountId, this.props.match.params.draftOrderId);
        }
    }

    render() {
        const { t, draft, match, isNotFound, isLoading } = this.props;
        const hasCustomer = draft?.order.customer && Object.keys(draft.order.customer).length > 0;
        const hasMetadata = draft?.order.metadata && Object.keys(draft.order.metadata).length > 0;
        return (
            <Page
                title={
                    <React.Fragment>
                        <Back />
                        <Title>{t('orders.draft_order.title')}</Title>
                    </React.Fragment>
                }
                hasEnvFrame={true}
            >
                {draft && (
                    <MaxWidth>
                        <DraftOrderId draft={draft} />
                        <Items draftOrOrder={draft} />
                        <ColumnsWrapper>
                            <Column>
                                {draft.order.itinerary && draft.order.itinerary.length > 0 && (
                                    <Itinerary itinerary={draft.order.itinerary} />
                                )}
                                {draft.events && <DraftOrderHistory draft={draft} />}
                                {draft.order.external_links && <ExternalLinks draftOrOrder={draft} accountId={this.props.match.params.accountId} />}
                            </Column>
                            <Column>
                                {hasCustomer && <Customer draftOrOrder={draft} />}
                                {hasMetadata && <Metadata draftOrOrder={draft} />}
                            </Column>
                        </ColumnsWrapper>
                    </MaxWidth>
                )}
                {!draft && (
                    <Card title={undefined} noPadding>
                        <Wrapper>
                            {isNotFound && <NotFound id={match.params.draftOrderId} title="orders.fields.draft_order" message="orders.draft_order.not_found.message" callToAction="orders.draft_order.not_found.call_to_action" link="../drafts"  />}
                            {isLoading && <LoadingOverlay />}
                        </Wrapper>
                    </Card>
                )}
            </Page>
        );
    }
}

const Wrapper = styled.div`
    position: relative;
    min-height: calc(${distances.ginormous} + ${distances.large});
`;

const Title = styled(H1)`
    color: ${colors.text};
    display: inline-block;
`;

const MaxWidth = styled.div`
    position: relative;
    width: 100%;
    max-width: 1200px;
`;

const ColumnsWrapper = styled.div`
    display: flex;
    position: relative;
    width: 100%;
    max-width: 1200px;

    @media (max-width: 1200px) {
        flex-direction: column;
    }
`;

const Column = styled.div`
    width: 50%;
    display: flex;
    flex-direction: column;
    min-height: 100px;

    @media (min-width: 1201px) {
        &:first-child {
            margin-right: ${distances.small};
        }
        &:last-child {
            margin-left: ${distances.small};
        }
    }

    @media (max-width: 1200px) {
        width: 100%;
    }
`;

export default withTranslation()(DraftOrderPage);
